import { FormComparison } from './insightsPlayerSeason';
import { TwPlayerMatchLine } from './insightsTwPlayerTables';

/*
 * Form / trends / consistency for the player profile, from TW match lines (newest first).
 */

const average = (values: number[]): number =>
    values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

const stdev = (values: number[]): number => {
    if (values.length < 2) return 0;
    const mean = average(values);
    const variance =
        values.reduce((sum, value) => sum + (value - mean) * (value - mean), 0) /
        (values.length - 1);
    return Math.sqrt(variance);
};

const median = (values: number[]): number => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

export type PlayerTrendMetric =
    | 'goals'
    | 'shots'
    | 'shootingPct'
    | 'assists'
    | 'exclusionsDrawn'
    | 'turnovers'
    | 'saves'
    | 'savePct';

export const FIELD_TREND_METRICS: { value: PlayerTrendMetric; label: string }[] = [
    { value: 'goals', label: 'Goals' },
    { value: 'shots', label: 'Shots' },
    { value: 'shootingPct', label: 'Shooting %' },
    { value: 'assists', label: 'Assists' },
    { value: 'exclusionsDrawn', label: 'Exclusions drawn' },
    { value: 'turnovers', label: 'Turnovers' },
];

export const GK_TREND_METRICS: { value: PlayerTrendMetric; label: string }[] = [
    { value: 'saves', label: 'Saves' },
    { value: 'savePct', label: 'Save %' },
];

export const pickTrendValue = (line: TwPlayerMatchLine, metric: PlayerTrendMetric): number => {
    const board = line.board;
    switch (metric) {
        case 'goals':
            return board.shots.total.goals;
        case 'shots':
            return board.shots.total.attempts;
        case 'shootingPct': {
            const a = board.shots.total.attempts;
            return a > 0 ? Math.round((board.shots.total.goals / a) * 100) : 0;
        }
        case 'assists':
            return board.assists;
        case 'exclusionsDrawn':
            return board.exclusionsDrawn;
        case 'turnovers':
            return board.turnovers;
        case 'saves':
            return board.saves.total.saves;
        case 'savePct': {
            const a = board.saves.total.attempts;
            return a > 0 ? Math.round((board.saves.total.saves / a) * 100) : 0;
        }
        default:
            return 0;
    }
};

/** Last N vs filtered sample averages (field). */
export const buildTwFieldFormComparison = (
    lines: TwPlayerMatchLine[],
    recentCount = 5
): FormComparison[] => {
    const recent = lines.slice(0, recentCount);
    const metric = (
        label: string,
        pick: (line: TwPlayerMatchLine) => number,
        lowerIsBetter?: boolean
    ): FormComparison => {
        const recentValue = average(recent.map(pick));
        const seasonValue = average(lines.map(pick));
        return {
            label,
            recent: recentValue,
            season: seasonValue,
            delta: recentValue - seasonValue,
            lowerIsBetter,
        };
    };

    return [
        metric('Goals', (line) => line.board.shots.total.goals),
        metric('Shots', (line) => line.board.shots.total.attempts),
        metric('Assists', (line) => line.board.assists),
        metric('Exclusions drawn', (line) => line.board.exclusionsDrawn),
        metric('Turnovers', (line) => line.board.turnovers, true),
    ];
};

/** GK form: saves + save % last N vs sample. */
export const buildTwGkFormComparison = (
    lines: TwPlayerMatchLine[],
    recentCount = 5
): FormComparison[] => {
    const recent = lines.slice(0, recentCount);
    const metric = (
        label: string,
        pick: (line: TwPlayerMatchLine) => number,
        lowerIsBetter?: boolean
    ): FormComparison => {
        const recentValue = average(recent.map(pick));
        const seasonValue = average(lines.map(pick));
        return {
            label,
            recent: recentValue,
            season: seasonValue,
            delta: recentValue - seasonValue,
            lowerIsBetter,
        };
    };

    const savePct = (line: TwPlayerMatchLine): number => {
        const a = line.board.saves.total.attempts;
        return a > 0 ? (line.board.saves.total.saves / a) * 100 : 0;
    };

    return [
        metric('Saves', (line) => line.board.saves.total.saves),
        metric('Save %', savePct),
        metric('SOG faced', (line) => line.board.saves.total.attempts),
    ];
};

export interface PlayerTrendPoint {
    matchId: number;
    label: string;
    value: number;
    opponentName?: string;
    when?: string;
    resultLine?: string;
}

const shortDate = (value?: Date): string | undefined => {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/** Chronological ascending for charts (input lines are newest-first). */
export const buildPlayerTrendSeries = (
    lines: TwPlayerMatchLine[],
    metric: PlayerTrendMetric
): PlayerTrendPoint[] =>
    [...lines].reverse().map((line, index) => ({
        matchId: line.matchId,
        label: `R${index + 1}`,
        value: pickTrendValue(line, metric),
        opponentName: line.opponentName,
        when: shortDate(line.startDate),
        resultLine:
            line.result && line.goalsFor != null && line.goalsAgainst != null
                ? `${line.result} ${line.goalsFor}:${line.goalsAgainst}`
                : line.result,
    }));

export interface ConsistencyStats {
    average: number;
    median: number;
    stdev: number;
    best: number;
    worst: number;
    aboveAverage: number;
    belowAverage: number;
    sample: number;
}

export const buildConsistencyStats = (
    lines: TwPlayerMatchLine[],
    metric: PlayerTrendMetric
): ConsistencyStats | undefined => {
    if (lines.length === 0) return undefined;
    const values = lines.map((line) => pickTrendValue(line, metric));
    const avg = average(values);
    return {
        average: avg,
        median: median(values),
        stdev: stdev(values),
        best: Math.max(...values),
        worst: Math.min(...values),
        aboveAverage: values.filter((v) => v > avg).length,
        belowAverage: values.filter((v) => v < avg).length,
        sample: values.length,
    };
};

export const formatFormValue = (value: number, label: string): string => {
    if (label.includes('%')) return `${Math.round(value)}%`;
    return Number.isInteger(value) ? `${value}` : value.toFixed(1);
};

export const formatFormDelta = (row: FormComparison): string => {
    const sign = row.delta > 0 ? '+' : '';
    if (row.label.includes('%')) return `${sign}${Math.round(row.delta)}pp`;
    const abs = Math.abs(row.delta);
    const text = abs >= 10 ? `${Math.round(row.delta)}` : row.delta.toFixed(1);
    return `${sign}${text}`;
};

export const formDeltaClass = (row: FormComparison): string => {
    if (Math.abs(row.delta) < 0.05) return 'isFlat';
    const improved = row.lowerIsBetter ? row.delta < 0 : row.delta > 0;
    return improved ? 'isUp' : 'isDown';
};
